/**
 * One predicate language for the model.
 *
 * "Show this only when …" was expressed six different ways across the config —
 * `condition`, `when`, `states`, `mode`, `minLength`, `equalsProfile` — each with
 * its own shape and its own evaluator, and the evaluators disagreed with one
 * another about the same input. This module is the single answer.
 *
 * The vocabulary is deliberately the server rule engine's, not a new one:
 * `{ field, operator, value | valueFrom }` with `equals` / `not_equals` / `in` /
 * `not_in` / `exists` / `not_exists`. A customer authoring a ruleset (30.03.15)
 * and a customer hiding a form field are then learning the same thing once.
 *
 * ## Shape
 *
 *   { field: 'typeId', operator: 'equals', value: 'bank' }
 *   { field: 'lines',  operator: 'minLength', value: 1 }
 *   { field: 'customerCountry', operator: 'equals', valueFrom: 'profile.currentOrgCountry' }
 *   { all: [ … ] }        every entry must hold
 *   { any: [ … ] }        at least one must hold
 *   { some: 'lines', … }  at least one row of `lines` satisfies the rest
 *   { none: 'lines', … }  no row of `lines` does
 *
 * ## Quantifiers
 *
 * `all` and `any` compose conditions over ONE subject. `some` and `none` change
 * the subject: they name a child array, and the rest of the object is evaluated
 * against each row of it in turn. Inside, `field` reads the row and `parent`
 * reads what the subject was before — so a row can be compared against the
 * record that holds it:
 *
 *   { some: 'reservations', all: [
 *       { field: 'documentId', operator: 'equals', valueFrom: 'context.docId' },
 *       { field: 'lineIndex',  operator: 'equals', valueFrom: 'context.lineIndex' } ] }
 *
 * Both polarities have a name because a config cannot negate. `any` and `all`
 * set that precedent; leaving `none` out would have meant one of its two callers
 * writing its half in code, which is the defect this module exists to remove.
 *
 * ## Subjects
 *
 * A bare `field` reads the record or form state. A `$`-prefixed field reads
 * render context supplied by the component — things that are not data:
 *
 *   $mode      'new' | 'edit'      which form mode is rendering
 *   $state     the record's workflow state
 *   $allDone   every line complete  (processView)
 *   $done      this line complete   (processView)
 *   $reservations  the document's reservations, a sibling of the row being
 *              judged rather than a child of it (reservationList)
 *
 * The prefix is what keeps `{ field: 'mode' }` — a real column on some entity —
 * from colliding with the renderer's mode.
 */

/** Operator names, shared with the server rule engine. */
export { OPERATORS, UNARY_OPERATORS } from './operators.js'
import { applyOperator } from './operators.js'

/** Read a dotted path out of an object, without throwing on a missing branch. */
function readPath(root, path) {
    if (!root || typeof path !== 'string') return undefined
    let node = root
    for (const seg of path.split('.')) {
        if (node === null || node === undefined) return undefined
        node = node[seg]
    }
    return node
}

/**
 * The value a condition is testing.
 * `$foo` comes from render context; anything else from the data.
 */
export function subjectValue(field, sources) {
    if (typeof field !== 'string' || field === '') return undefined
    if (field.charCodeAt(0) === 36 /* $ */) return sources?.context?.[field.slice(1)]
    return sources?.values?.[field]
}

/** The value a condition compares against — a literal, or resolved from sources. */
function comparisonValue(condition, sources) {
    if (condition.valueFrom !== undefined) return readPath(sources, condition.valueFrom)
    return condition.value
}

const compare = (operator, actual, expected) => applyOperator(operator, actual, expected)

/**
 * Evaluate the rest of a quantifier against each row of the array it names.
 *
 * The array is read with `subjectValue`, so `some: 'reservations'` reads the
 * data and `some: '$reservations'` reads render context — the same rule a
 * `field` follows, rather than a second one to remember.
 *
 * Anything that is not an array answers false: an absent list has no row that
 * satisfies anything. That matches `any: []`, and it is what the hand-written
 * `item.reservations?.some(…)` this replaces already did.
 *
 * Inside, `values` is the row and `parent` is whatever `values` was outside, so
 * `valueFrom: 'parent.x'` compares a row against its holder. Every other source
 * — `context`, `profile` — is carried through untouched.
 */
function quantify(condition, key, sources) {
    const rows = subjectValue(condition[key], sources)
    if (!Array.isArray(rows)) return false

    const { [key]: _subject, ...inner } = condition
    return rows.some(row => evaluateCondition(inner, {
        ...sources,
        values: row,
        parent: sources?.values,
    }))
}

/**
 * Does this condition hold?
 * A missing or malformed condition holds — nothing is hidden by accident.
 */
export function evaluateCondition(condition, sources) {
    if (!condition || typeof condition !== 'object') return true

    // A quantifier moves the subject to a child array before anything else is
    // read, so it has to be tested before the combinators — the rest of the
    // object is the condition each row is judged by, not a sibling clause.
    if (typeof condition.some === 'string') return quantify(condition, 'some', sources)
    if (typeof condition.none === 'string') return !quantify(condition, 'none', sources)

    if (Array.isArray(condition.all)) {
        return condition.all.every(c => evaluateCondition(c, sources))
    }
    if (Array.isArray(condition.any)) {
        return condition.any.some(c => evaluateCondition(c, sources))
    }
    // A bare array is an implicit `all`.
    if (Array.isArray(condition)) {
        return condition.every(c => evaluateCondition(c, sources))
    }
    if (condition.field === undefined) return true

    const actual = subjectValue(condition.field, sources)
    const expected = comparisonValue(condition, sources)
    return compare(condition.operator ?? 'equals', actual, expected)
}

/**
 * Every field a condition reads, so a component can subscribe to exactly those.
 * `$`-prefixed subjects are excluded — they come from props, not the store.
 *
 * A quantifier contributes the array it names and nothing beneath it. The fields
 * inside belong to a row, not to the store, and subscribing to `documentId`
 * because some reservation has one would watch a key the record does not have.
 * The array is the right granularity anyway: if any row changes, it changes.
 */
export function conditionFields(condition) {
    const out = []
    const add = (field) => {
        if (typeof field !== 'string' || field === '' || field.charCodeAt(0) === 36) return
        if (!out.includes(field)) out.push(field)
    }
    // A condition comparing one field to a sibling — `valueFrom: 'values.other'`
    // — reads `other` from the same scope as `field`, so a subscriber has to
    // watch it too or a change to `other` alone would go unnoticed. `profile.*`
    // and `context.*` valueFrom targets aren't form state, so they're left alone.
    const addValueFrom = (valueFrom) => {
        if (typeof valueFrom === 'string' && valueFrom.startsWith('values.')) add(valueFrom.slice('values.'.length))
    }
    const visit = (c) => {
        if (!c || typeof c !== 'object') return
        if (Array.isArray(c)) { c.forEach(visit); return }
        if (typeof c.some === 'string') { add(c.some); return }
        if (typeof c.none === 'string') { add(c.none); return }
        if (Array.isArray(c.all)) c.all.forEach(visit)
        if (Array.isArray(c.any)) c.any.forEach(visit)
        add(c.field)
        addValueFrom(c.valueFrom)
    }
    visit(condition)
    return out
}

/**
 * What should the renderer do with this element?
 *
 *   'show'      condition holds (or there is none)
 *   'collapse'  hidden, and its space goes with it — the default
 *   'reserve'   hidden, but its box stays so the layout does not reflow.
 *               Only meaningful where the element occupies a cell in a row;
 *               `lint:components` rejects `collapse: false` elsewhere.
 */
export function resolveVisibility(item, sources) {
    if (!item || typeof item !== 'object') return 'show'
    if (evaluateCondition(item.condition, sources)) return 'show'
    return item.collapse === false ? 'reserve' : 'collapse'
}
